import { Link } from "react-router-dom"
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Slide,
  Zoom,
  Button,
  Fab,
  useTheme,
  Paper,
  CardMedia,
} from "@mui/material"
import _ from "lodash"
import { Trans } from "react-i18next"
import { isValidURL } from "utils/url"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"
import { TopBarContainer } from "containers/TopBarContainer"
import { BackIcon, PrevStepIcon, NextStepIcon } from "components/icons"
import { VerticalDirectionImageDialog } from "components/Dialog/VerticalDirectionImageDialog"
import { StepProgressBar } from "components/Progress/StepProgressBar"
import { OrdinalSelector } from "components/OrdinalSelector"
import { ExitIcon } from "components/icons"
import { useStepByStep } from "hooks/direction/useStepByStep"
import { useCallback, useMemo, useRef, useState } from "react"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"
import { getIcon } from "utils/icon"

export const StepByStep = () => {
  const { isMultiOrdinalVenue } = useVenue()
  const contentRef = useRef(null)
  const [slideEntered, setSlideEntered] = useState(false)
  const offsetBottom = +contentRef.current?.clientHeight || 0
  const {
    route,
    steps,
    destinationParam,
    originParam,
    currentStepNumber,
    currentStep,
    currentOriginStepLevelName,
    currentDestStepLevelName,
    currentLevel,
    currentOrdinal,
    originLevelName,
    originName,
    originVenueLogo,
    destinationLevelName,
    currentVerticalStepMethod,
    destinationName,
    destinationVenueLogo,
    isReady,
    hasNextStep,
    hasPrevStep,
    exitPath,
    goToStepByIndex,
    goToPrevStep,
    goToNextStep,
  } = useStepByStep()
  const stepsAvailable = steps && steps.length > 0
  useStepByStepMap(route, currentStepNumber, {
    offset: { bottom: offsetBottom },
    enabled: isReady && slideEntered,
  })

  const { t } = useTranslation()

  const theme = useTheme()

  const {
    container,
    primaryText,
    title,
    secondaryText,
    locationText,
    navButtonWrapper,
    headerWrapper,
    navButton,
    iconNavButton,
    overviewWrapper,
    wrapper,
    kioskBackButton,
    highlightedText,
    stepTextContainer,
    exitButtonWrapper,
    exitButton,
    stepProgressBar,
    stepDetailStack,
    venueLogowrapper,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}
  const {
    root: topBarRoot,
    ordinalSelector,
    backButton,
  } = theme.components[`AppTopBar`]?.styleOverrides || {}

  const originFloorText = isMultiOrdinalVenue
    ? `(${t("At Floor", {
        value: originLevelName,
      })})`
    : ""
  const destinationFloorText = isMultiOrdinalVenue
    ? `(${t("At Floor", {
        value: destinationLevelName,
      })})`
    : ""
  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const handleSlideEnter = useCallback(
    () => !slideEntered && setSlideEntered(true),
    [slideEntered]
  )

  const verticalDirectionImageUrl = useMemo(() => {
    const src = _.get(
      theme?.assets?.verticalStep?.[currentVerticalStepMethod],
      "url",
      ""
    )
    return isValidURL(src) ? src : sanitizePublicPath(src)
  }, [currentVerticalStepMethod, theme?.assets?.verticalStep])

  const showVerticalStepDialog = !!verticalDirectionImageUrl

  const prevStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigationStep?.prevIcon) || (
        <PrevStepIcon fontSize="inherit" />
      ),
    [theme]
  )
  const nextStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigationStep?.nextIcon) || (
        <NextStepIcon fontSize="inherit" />
      ),
    [theme]
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <BackIcon />,
    [theme]
  )

  return (
    <>
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={currentOriginStepLevelName}
        destinationLevelName={currentDestStepLevelName}
        open={showVerticalStepDialog}
      />
      {/* Header Section */}
      <TopBarContainer sx={topBarRoot}>
        <Stack spacing={5}>
          {/* Back Button */}
          <Fab
            sx={backButton}
            size="medium"
            component={Link}
            replace={true}
            to={`/maps/dir/${originParam}/${destinationParam}`}
          >
            {backIcon}
          </Fab>
          {/* only show ordinalSelector when there is no VerticalDirectionImageDialog showing */}
          {isMultiOrdinalVenue && !showVerticalStepDialog && (
            <Box sx={ordinalSelector}>
              <OrdinalSelector
                label="Floor"
                levels={currentLevel}
                selectedOrdinal={currentOrdinal}
              />
            </Box>
          )}
        </Stack>
      </TopBarContainer>
      {/* End of Header Section */}
      <Slide direction="up" in={true} onEnter={handleSlideEnter}>
        <Box sx={wrapper}>
          <Fab
            component={Link}
            variant="rounded"
            replace={true}
            to={`/maps/dir/${originParam}/${destinationParam}`}
            sx={kioskBackButton}
          >
            {backIcon}
          </Fab>
          <Paper sx={container} spacing={1} ref={contentRef}>
            <Box sx={exitButtonWrapper}>
              <Button
                component={Link}
                to={exitPath}
                startIcon={<ExitIcon />}
                sx={exitButton}
                variant="contained"
              >
                {t("Exit")}
              </Button>
            </Box>
            <Stack width="100%" direction="column" sx={stepDetailStack}>
              <Box sx={headerWrapper}>
                <Stack alignItems="center" spacing={1}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Zoom in={hasPrevStep}>
                      <IconButton
                        color="inherit"
                        sx={iconNavButton}
                        onClick={goToPrevStep}
                      >
                        {prevStepIcon}
                      </IconButton>
                    </Zoom>

                    <Typography component={"h1"} textAlign="center" sx={title}>
                      {t("Process step", {
                        currentStep: currentStepNumber,
                        totalSteps: steps.length,
                      })}
                    </Typography>

                    <Zoom in={hasNextStep}>
                      <IconButton
                        color="inherit"
                        sx={iconNavButton}
                        onClick={goToNextStep}
                      >
                        {nextStepIcon}
                      </IconButton>
                    </Zoom>
                  </Stack>
                  <Box sx={stepTextContainer}>
                    <Typography
                      variant="subTitle"
                      component="p"
                      sx={primaryText}
                    >
                      <Trans
                        components={{
                          category: highlightedStepText,
                          destination: highlightedStepText,
                          landmark: highlightedStepText,
                        }}
                      >
                        {t(currentStep?.description.text, {
                          category: t(
                            currentStep?.description.options?.category
                          ),
                          destination: t(
                            currentStep?.description.options?.destination
                          ),
                          landmark: t(
                            currentStep?.description.options?.landmark
                          ),
                        })}
                      </Trans>
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              {/* mobile progress bar */}
              <StepProgressBar
                steps={steps}
                currentStep={currentStepNumber}
                sx={stepProgressBar}
                handleClickStep={goToStepByIndex}
                enableAnimateScroll={slideEntered && stepsAvailable}
              />
              {/* end of mobile progress bar */}
              {/* Location and overview section */}
              <Stack sx={overviewWrapper}>
                <Box sx={venueLogowrapper}>
                  <CardMedia image={originVenueLogo} />
                </Box>
                <Typography
                  textAlign="start"
                  variant="caption"
                  sx={locationText}
                >
                  {t(originName)}
                </Typography>
                <Typography variant="caption" sx={secondaryText}>
                  {t("From To", {
                    origin: `${t(originName)} ${originFloorText}`,
                    destination: `${t(
                      destinationName
                    )} ${destinationFloorText}`,
                  })}
                </Typography>
                <Box sx={venueLogowrapper}>
                  <CardMedia image={destinationVenueLogo} />
                </Box>
                <Typography textAlign="end" variant="caption" sx={locationText}>
                  {t(destinationName)}
                </Typography>
              </Stack>
              <Stack></Stack>
              <Stack sx={navButtonWrapper}>
                <Button
                  variant="outlined"
                  startIcon={<PrevStepIcon />}
                  disabled={!hasPrevStep}
                  sx={navButton}
                  onClick={goToPrevStep}
                >
                  {t("Previous Step")}
                </Button>
                <Button
                  variant="outlined"
                  disabled={!hasNextStep}
                  sx={navButton}
                  endIcon={<NextStepIcon />}
                  onClick={goToNextStep}
                >
                  {t("Next Step")}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Slide>
    </>
  )
}
